<template>
  <div class="facility-page invisible-scrollbar">
    <header class="facility-heading">
      <h1>{{ $t("facilityPage.title") }}</h1>
      <p>{{ $t("facilityPage.updatedAt") }}: {{ currentTime }}</p>
    </header>

    <section class="facility-toolbar" :aria-label="$t('facilityPage.actions')">
      <div class="facility-search">
        <input
          v-model.trim="search"
          type="search"
          :placeholder="$t('facilityPage.search')"
          :aria-label="$t('facilityPage.searchFacility')"
          @keydown.enter="searchDataFacility"
        />
        <button type="button" :aria-label="$t('facilityPage.search')" @click="searchDataFacility">
          <v-icon color="white" size="24">mdi-magnify</v-icon>
        </button>
      </div>
      <button
        class="create-facility"
        type="button"
        @click="openCreateDataFacility"
      >
        {{ $t("facilityPage.create") }} +
      </button>
    </section>

    <section class="facility-panel">
      <div v-if="filteredFacilities.length" class="facility-grid">
        <article
          v-for="item in filteredFacilities"
          :key="item.id"
          class="room-card"
        >
          <div class="room-cover">
            <img
              :src="item.image || defaultFacilityPreview"
              :alt="item.name"
            />
            <div class="room-overlay">
              <h2>{{ item.name }}</h2>
              <span>{{ getLocationName(item) }}</span>
            </div>
          </div>
          <div class="room-detail">
            <h3>{{ $t("facilityPage.detail") }}</h3>
            <div
              v-if="getFacilityUtilities(item.utility).length"
              class="utility-grid"
            >
              <div
                v-for="utility in getFacilityUtilities(item.utility)"
                :key="utility.id || utility.utilityID"
              >
                <v-icon color="#00368e" size="20">{{
                  getUtilityIcon(utility.name)
                }}</v-icon>
                <strong>{{ utility.amount }}</strong>
                <span>{{ utility.name }}</span>
              </div>
            </div>
            <p v-else class="no-utility">{{ $t("facilityPage.noUtilities") }}</p>
            <div class="room-actions">
              <v-btn
                class="delete-button"
                depressed
                :disabled="!item.active || DeletingFacilityID === item.id"
                @click="deleteDataFacilityByID(item.id)"
                >{{ $t("facilityPage.delete") }}</v-btn
              >
              <v-btn
                class="edit-button"
                depressed
                @click="openEditDataFacility(item.id)"
                >{{ $t("facilityPage.edit") }}</v-btn
              >
            </div>
          </div>
        </article>
      </div>
      <div v-else class="empty-facility">
        <v-icon size="48" color="#9aa6b8">mdi-office-building-outline</v-icon>
        <p>{{ $t("facilityPage.noFacilities") }}</p>
      </div>

      <footer class="facility-pagination">
        <v-pagination
          v-model="page"
          :total-visible="3"
          :length="pageCount"
          color="#00368e"
          @input="ChangePage(page)"
        />
        <v-select
          v-model="itemsPerPage"
          class="page-size"
          :items="items"
          dense
          solo
          flat
          hide-details
          @input="ChangePerPage(itemsPerPage)"
        />
      </footer>
    </section>

    <v-dialog
      v-model="FacilityDialog"
      max-width="1254"
      persistent
      content-class="edit-facility-dialog"
    >
      <v-card class="facility-card">
        <v-card-title class="facility-header">
          <span>
            {{
              isCreateMode
                ? $t("facilityPage.createFacility")
                : $t("facilityPage.editFacility")
            }}
          </span>
          <button
            type="button"
            :aria-label="$t('facilityPage.close')"
            @click="FacilityDialog = false"
          >
            <v-icon color="white" size="30">mdi-close</v-icon>
          </button>
        </v-card-title>

        <v-card-text class="edit-facility-content">
          <div class="edit-fields">
            <div class="edit-field">
              <label>{{ $t("facilityPage.facilityName") }}</label
              ><v-text-field
                v-model="FacilityName"
                outlined
                dense
                hide-details
              />
            </div>
            <div class="edit-field">
              <label>{{ $t("facilityPage.roomType") }}</label
              ><v-select
                v-model="RoomType"
                :items="roomTypeItems"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
              />
            </div>
            <div class="edit-field">
              <label>{{ $t("facilityPage.eventType") }}</label
              ><v-select
                v-model="EventType"
                :items="eventTypeItems"
                item-text="text"
                item-value="value"
                outlined
                dense
                hide-details
              />
            </div>
            <div class="edit-field">
              <label>{{ $t("facilityPage.location") }}</label
              ><v-select
                v-model="Location"
                :items="LocationList"
                item-value="locationID"
                :item-text="getLocationName"
                outlined
                dense
                hide-details
                :aria-label="$t('facilityPage.location')"
              />
            </div>
            <div class="edit-field">
              <label>{{ $t("facilityPage.floor") }}</label
              ><v-select
                v-model="ZoneID"
                :items="filteredZoneList"
                item-value="zoneID"
                :item-text="getZoneName"
                outlined
                dense
                hide-details
                :aria-label="$t('facilityPage.floor')"
              />
            </div>
            <div class="edit-field">
              <label>{{ $t("facilityPage.startTime") }}</label>
              <v-text-field
                v-model="OpenTime"
                type="datetime-local"
                outlined
                dense
                hide-details
              />
            </div>
            <div class="edit-field">
              <label>{{ $t("facilityPage.endTime") }}</label>
              <v-text-field
                v-model="EndTime"
                type="datetime-local"
                outlined
                dense
                hide-details
              />
            </div>
          </div>

          <div class="edit-preview-column">
            <div class="edit-image">
              <img :src="urlImg" :alt="$t('facilityPage.facilityPreview')" />
              <label class="image-upload" :aria-label="$t('facilityPage.changeImage')">
                <v-icon color="white" size="24">mdi-image-plus</v-icon>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/bmp"
                  @change="selectFacilityImage"
                />
              </label>
            </div>

            <div
              class="facility-status active-status-control"
              role="group"
              :aria-label="$t('facilityPage.facilityStatus')"
            >
              <button
                type="button"
                :class="{ selected: Active === true || Active === 'Active' }"
                @click="Active = true"
              >
                {{ $t("facilityPage.active") }}
              </button>
              <button
                type="button"
                :class="{
                  selected: Active === false || Active === 'InActive',
                }"
                @click="Active = false"
              >
                {{ $t("facilityPage.inactive") }}
              </button>
            </div>
          </div>

          <div class="edit-utilities">
            <v-data-table
              class="utility-table"
              :headers="headers"
              :items="UtilitysList"
              :items-per-page="-1"
              item-key="utilityID"
              hide-default-footer
              disable-sort
            >
              <template v-slot:item.no="{ index }">{{ index + 1 }}</template>
              <template v-slot:item.amount="{ item }"
                ><v-text-field
                  v-model.number="item.amount"
                  type="number"
                  min="0"
                  dense
                  solo
                  flat
                  hide-details
                  @blur="normalizeUtilityAmount(item)"
              /></template>
              <template v-slot:item.action="{ item }"
                ><button
                  class="utility-delete-action"
                  type="button"
                  :aria-label="$t('facilityPage.deleteUtility')"
                  @click="item.amount = 0"
                >
                  <img
                    src="@/assets/icons/bag.svg"
                    alt=""
                    aria-hidden="true"
                  />
                </button></template
              >
              <template v-slot:no-data
                ><div class="table-empty">{{ $t("facilityPage.noUtilityData") }}</div></template
              >
            </v-data-table>
          </div>

        </v-card-text>

        <v-alert v-if="UtilityDataError" type="warning" dense outlined role="status" class="mx-6">
          {{ UtilityDataError }}
          <v-btn v-if="isCreateMode" small text :disabled="LoadingDialog" @click="getDataUtilityListForCreate">Retry</v-btn>
        </v-alert>

        <v-card-actions class="edit-actions">
          <v-btn class="cancel-edit" depressed @click="FacilityDialog = false"
            >{{ $t("facilityPage.cancel") }}</v-btn
          >
          <v-btn
            class="save-edit"
            depressed
            :disabled="ImageProcessing || IsSubmitting || !UtilityDataReady"
            :loading="IsSubmitting"
            @click="saveDataFacility"
          >
            {{ isCreateMode ? $t("facilityPage.create") : $t("facilityPage.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >{{ $t("facilityPage.loading") }}</span
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { reportPageDataUpdated } from "@/utils/pageDataUpdated";
import enurl from "@/api/environment";
import defaultFacilityPreview from "@/assets/facility-preview-placeholder.jpg";
import Swal from "sweetalert2/dist/sweetalert2.js";
// import { Image } from "pdfjs";

export default {
  name: "Facility",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      items: [10, 15, 20, 50, 100],
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      currentTime: new Date().toLocaleTimeString("th-TH", { hour12: false }),
      no_run: 0,

      ListData: [],
      FacilityID: null,
      FacilityDialog: false,
      isCreateMode: false,
      LocationList: [],
      ZoneList: [],
      FacilityName: "",
      RoomType: null,
      EventType: null,
      Location: null,
      ZoneID: null,
      OpenTime: null,
      EndTime: null,
      FacilityImage: null,
      ImageProcessing: false,
      IsSubmitting: false,
      DeletingFacilityID: null,
      previewObjectUrl: null,
      urlImg: null,
      base64: "",
      Active: null,
      UtilitysList: [],
      AllUtilityList: [],
      UtilityDataReady: false,
      UtilityDataError: "",
      defaultFacilityPreview: defaultFacilityPreview,
    };
  },
  computed: {
    filteredZoneList() {
      if (this.Location == null) return [];
      return this.ZoneList.filter(
        (zone) => String(zone.locationID) === String(this.Location),
      );
    },
    roomTypeItems() {
      return [
        { text: this.$t("facilityPage.room"), value: "Room" },
        { text: this.$t("facilityPage.space"), value: "Space" },
      ];
    },
    eventTypeItems() {
      return [
        { text: this.$t("facilityPage.reserve"), value: "Reserve" },
      ];
    },
    filteredFacilities() {
      return this.ListData;
    },
    headers() {
      return [
        {
          text: this.$t("facilityPage.no"),
          align: "start",
          value: "no",
        },
        {
          text: this.$t("facilityPage.utilityName"),
          align: "start",
          value: "utilityName",
          sortable: false,
        },
        {
          text: this.$t("facilityPage.amount"),
          align: "start",
          value: "amount",
          sortable: false,
        },
        {
          text: this.$t("facilityPage.actions"),
          align: "center",
          value: "action",
          sortable: false,
        },
      ];
    },
  },

  watch: {
    Location(locationID) {
      if (
        this.ZoneID != null &&
        !this.ZoneList.some(
          (zone) =>
            String(zone.zoneID) === String(this.ZoneID) &&
            String(zone.locationID) === String(locationID),
        )
      ) {
        this.ZoneID = null;
      }
    },
    FacilityDialog(isOpen) {
      if (!isOpen) this.releasePreviewObjectUrl();
    },
  },

  async mounted() {
    let self = this;
    await self.getDataUtilityMaster();
    await self.getDataFacility();
    await self.getDataDropdownLocation();
    await self.getDataDropdownZone();
  },

  beforeDestroy() {
    this.releasePreviewObjectUrl();
  },

  methods: {
    getLocationName(item) {
      if (!item) return "";
      return this.$i18n.locale === "th"
        ? item.locationNameTH || item.locationName || ""
        : item.locationName || item.locationNameTH || "";
    },

    getZoneName(item) {
      if (!item) return "";
      return this.$i18n.locale === "th"
        ? item.zoneNameTH || item.zoneName || ""
        : item.zoneName || item.zoneNameTH || "";
    },

    async openCreateDataFacility() {
      this.releasePreviewObjectUrl();
      this.isCreateMode = true;
      this.FacilityID = null;
      this.FacilityName = "";
      this.RoomType = null;
      this.EventType = null;
      this.Location = null;
      this.ZoneID = null;
      this.OpenTime = null;
      this.EndTime = null;
      this.FacilityImage = null;
      this.urlImg = defaultFacilityPreview;
      this.base64 = "";
      this.Active = true;
      this.UtilitysList = [];
      this.UtilityDataReady = false;
      this.UtilityDataError = "";
      this.FacilityDialog = true;
      await this.getDataUtilityListForCreate();
    },
    async getDataUtilityListForCreate() {
      this.UtilityDataReady = false;
      this.UtilityDataError = "";
      this.LoadingDialog = true;
      try {
        const response = await axios.get(
          `${this.url}Facility/getDataUtilityList`,
        );
        this.throwIfSessionExpired(response);

        if (response.data.status !== 0 || !Array.isArray(response.data.data)) {
          throw new Error(response.data.message || this.$t("facilityPage.utilityLoadFailed"));
        }
        if (response.data.status == 0 && Array.isArray(response.data.data)) {
          this.AllUtilityList = response.data.data;
          this.UtilitysList = response.data.data.map((utility) => ({
            ...utility,
            amount: 0,
          }));
          this.UtilityDataReady = true;
        }
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("facilityPage.utilityLoadFailed");

        this.UtilityDataError = message;
        Swal.fire({
          icon: "error",
          title: this.$t("facilityPage.error"),
          width: 900,
          text: message,
        });
      } finally {
        this.LoadingDialog = false;
      }
    },
    async selectFacilityImage(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const allowedTypes = ["image/png", "image/jpeg", "image/bmp"];
      const maximumSize = 5 * 1024 * 1024;
      if (!allowedTypes.includes(file.type) || file.size > maximumSize) {
        event.target.value = "";
        await Swal.fire({
          icon: "warning",
          width: 500,
          text: this.$t("facilityPage.invalidImage"),
        });
        return;
      }

      this.ImageProcessing = true;
      try {
        this.FacilityImage = file;
        this.Preview_image();
        await this.createBase64Image(file);
      } catch (error) {
        this.FacilityImage = null;
        this.urlImg = defaultFacilityPreview;
        this.base64 = "";
        event.target.value = "";
        await Swal.fire({
          icon: "error",
          width: 500,
          text: error.message || this.$t("facilityPage.imageReadFailed"),
        });
      } finally {
        this.ImageProcessing = false;
      }
    },
    normalizeUtilityAmount(utility) {
      const amount = Number(utility.amount);
      utility.amount = Number.isFinite(amount) && amount > 0 ? amount : 0;
    },
    getUtilityIcon(name) {
      const utilityName = String(name || "").toLowerCase();
      if (utilityName.includes("projector")) return "mdi-projector";
      if (utilityName.includes("video")) return "mdi-video-outline";
      if (utilityName.includes("person")) return "mdi-account-outline";
      if (utilityName.includes("speaker")) return "mdi-microphone-outline";
      if (utilityName.includes("whiteboard")) return "mdi-pencil-outline";
      if (utilityName.includes("wi-fi") || utilityName.includes("wifi"))
        return "mdi-wifi";
      return "mdi-check-circle-outline";
    },
    getFacilityUtilities(utilities) {
      const facilityUtilities = Array.isArray(utilities) ? utilities : [];
      if (!this.AllUtilityList.length) {
        return facilityUtilities.map((utility) => {
          const amount = Number(utility.amount);
          return {
            ...utility,
            amount: Number.isFinite(amount) ? amount : 0,
          };
        }).filter((utility) => utility.amount > 0);
      }

      const facilityUtilityMap = new Map(
        facilityUtilities.map((utility) => [
          String(utility.utilityID || utility.id),
          utility,
        ]),
      );

      return this.AllUtilityList.map((utility) => {
        const facilityUtility = facilityUtilityMap.get(
          String(utility.utilityID),
        );
        const amount = Number(facilityUtility && facilityUtility.amount);
        return {
          ...facilityUtility,
          ...utility,
          name:
            (facilityUtility && facilityUtility.name) || utility.utilityName,
          amount: Number.isFinite(amount) ? amount : 0,
        };
      }).filter((utility) => utility.amount > 0);
    },
    async getDataUtilityMaster() {
      try {
        const response = await axios.get(
          `${this.url}Facility/getDataUtilityList`,
        );
        this.throwIfSessionExpired(response);
        if (response.data.status == 0 && Array.isArray(response.data.data)) {
          this.AllUtilityList = response.data.data;
        }
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("facilityPage.utilityLoadFailed");
        Swal.fire({
          icon: "error",
          title: this.$t("facilityPage.error"),
          width: 900,
          text: message,
        });
      }
    },
    validateFacility() {
      if (!this.FacilityName || !this.FacilityName.trim())
        return this.$t("facilityPage.facilityNameRequired");
      if (!this.RoomType) return this.$t("facilityPage.roomTypeRequired");
      if (!this.EventType) return this.$t("facilityPage.eventTypeRequired");
      if (this.Location == null) return this.$t("facilityPage.locationRequired");
      if (this.ZoneID == null) return this.$t("facilityPage.floorRequired");
      if (!this.OpenTime) return this.$t("facilityPage.startTimeRequired");
      if (!this.EndTime) return this.$t("facilityPage.endTimeRequired");
      const openTime = new Date(this.OpenTime).getTime();
      const endTime = new Date(this.EndTime).getTime();
      if (!Number.isFinite(openTime) || !Number.isFinite(endTime))
        return this.$t("facilityPage.invalidTime");
      if (openTime >= endTime)
        return this.$t("facilityPage.endAfterStart");
      return "";
    },
    throwIfSessionExpired(response) {
      const message = String(response?.data?.message || "");
      const normalizedMessage = message.toLowerCase();
      if (
        normalizedMessage.includes("session") &&
        normalizedMessage.includes("expired")
      ) {
        const error = new Error(message);
        error.isSessionExpired = true;
        this.$store.dispatch("ForceLogout", message);
        throw error;
      }
    },
    getErrorMessage(error, fallbackMessage) {
      return (
        error?.response?.data?.message ||
        error?.message ||
        fallbackMessage
      );
    },
    async showRequestError(error, fallbackMessage, width = 500) {
      if (error?.isSessionExpired || error?.response?.status === 401) return;
      await Swal.fire({
        icon: "error",
        title: this.$t("facilityPage.error"),
        width,
        text: this.getErrorMessage(error, fallbackMessage),
      });
    },
    buildFacilityPayload() {
      const utilityList = this.UtilitysList.map((utility) => {
        const utilityID = Number(utility.utilityID || utility.id);
        const amount = Number(utility.amount);
        return {
          utilityID: utilityID,
          utilityAmount:
            Number.isFinite(amount) && amount > 0 ? amount : 0,
        };
      }).filter((utility) => Number.isFinite(utility.utilityID));
      const primaryUtility = utilityList.find(
        (utility) => utility.utilityAmount > 0,
      );

      return {
        facilityName: this.FacilityName.trim(),
        eventType: this.EventType == "Reserve" ? 1 : null,
        roomType:
          this.RoomType == "Room" ? 1 : this.RoomType == "Space" ? 2 : null,
        location:
          this.Location && typeof this.Location === "object"
            ? this.Location.locationID
            : this.Location,
        zoneID: this.ZoneID,
        utilityID: primaryUtility ? primaryUtility.utilityID : null,
        openTime: this.OpenTime,
        endTime: this.EndTime,
        image: this.base64 || "",
        active: this.Active === true || this.Active === "Active",
        utilityList: utilityList,
      };
    },
    async saveDataFacility() {
      if (this.IsSubmitting || !this.UtilityDataReady) return;
      const validationMessage = this.validateFacility();
      if (validationMessage) {
        await Swal.fire({
          icon: "warning",
          width: 500,
          text: validationMessage,
        });
        return;
      }

      this.IsSubmitting = true;
      this.LoadingDialog = true;
      try {
        const payload = this.buildFacilityPayload();
        const endpoint = this.isCreateMode
          ? "Facility/addDataFacility"
          : "Facility/updateDataFacility";
        if (!this.isCreateMode) payload.facilityID = this.FacilityID;

        const response = await axios.post(`${this.url}${endpoint}`, payload);
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) {
          throw new Error(
            response.data.message || this.$t("facilityPage.saveFailed"),
          );
        }

        await Swal.fire({
          icon: "success",
          title: this.isCreateMode
            ? this.$t("facilityPage.created")
            : this.$t("facilityPage.updated"),
          text: this.isCreateMode
            ? this.$t("facilityPage.createSuccess")
            : this.$t("facilityPage.updateSuccess"),
          showConfirmButton: false,
          timer: 1500,
        });
        if (this.isCreateMode) this.page = 1;
        this.FacilityDialog = false;
        this.currentTime = new Date().toLocaleTimeString("th-TH", {
          hour12: false,
        });
        await this.getDataFacility();
      } catch (error) {
        await this.showRequestError(
          error,
          this.$t("facilityPage.saveFailed"),
        );
      } finally {
        this.IsSubmitting = false;
        this.LoadingDialog = false;
      }
    },
    async deleteDataFacilityByID(facilityID) {
      if (this.DeletingFacilityID !== null) return;
      const confirmation = await Swal.fire({
        icon: "warning",
        title: this.$t("facilityPage.deleteConfirmTitle"),
        text: this.$t("facilityPage.deleteConfirmText"),
        showCancelButton: true,
        confirmButtonText: this.$t("facilityPage.delete"),
        cancelButtonText: this.$t("facilityPage.cancel"),
        confirmButtonColor: "#d00000",
      });
      if (!confirmation.isConfirmed) return;

      this.DeletingFacilityID = facilityID;
      this.LoadingDialog = true;
      try {
        const response = await axios.post(
          `${this.url}Facility/deleteDataFacilityByID`,
          { facilityID: facilityID },
        );
        this.throwIfSessionExpired(response);
        if (response.data.status != 0) {
          throw new Error(
            response.data.message || this.$t("facilityPage.deleteFailed"),
          );
        }

        await Swal.fire({
          icon: "success",
          title: this.$t("facilityPage.deleted"),
          text: this.$t("facilityPage.deleteSuccess"),
          showConfirmButton: false,
          timer: 1500,
        });
        if (this.ListData.length === 1 && this.page > 1) this.page -= 1;
        await this.getDataFacility();
      } catch (error) {
        await this.showRequestError(
          error,
          this.$t("facilityPage.deleteFailed"),
        );
      } finally {
        this.DeletingFacilityID = null;
        this.LoadingDialog = false;
      }
    },
    async getDataFacility() {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        search: self.search || "",
        page: self.page,
        perpage: self.itemsPerPage,
      };
      await axios
        .post(`${self.url}Facility/getDataFacility`, temp)
        .then(function (response) {
          self.throwIfSessionExpired(response);
          if (response.data.status == 0) {
            self.ListData = response.data.data.listData;
            self.pageCount = response.data.data.count;
            self.no_run = (self.page - 1) * self.itemsPerPage;
            reportPageDataUpdated(self);
          }
        })
        .catch(function (error) {
          self.showRequestError(
            error,
            self.$t("facilityPage.loadFailed"),
            900,
          );
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    ChangePage(value) {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        search: self.search || "",
        page: value,
        perpage: self.itemsPerPage,
      };
      axios
        .post(`${self.url}Facility/getDataFacility`, temp)
        .then(function (response) {
          self.throwIfSessionExpired(response);
          if (response.data.status == 0) {
            self.ListData = response.data.data.listData;
            self.pageCount = response.data.data.count;
            self.no_run = (self.page - 1) * self.itemsPerPage;
            reportPageDataUpdated(self);
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: self.$t("facilityPage.error"),
            width: 900,
            text:
              error?.response?.data?.message ||
              error?.message ||
              self.$t("facilityPage.loadFailed"),
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    ChangePerPage(value) {
      let self = this;
      self.page = 1;
      self.LoadingDialog = true;
      let temp = {
        search: self.search || "",
        page: 1,
        perpage: value,
      };
      axios
        .post(`${self.url}Facility/getDataFacility`, temp)
        .then(function (response) {
          self.throwIfSessionExpired(response);
          if (response.data.status == 0) {
            self.ListData = response.data.data.listData;
            self.pageCount = response.data.data.count;
            self.no_run = (self.page - 1) * self.itemsPerPage;
            reportPageDataUpdated(self);
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: self.$t("facilityPage.error"),
            width: 900,
            text:
              error?.response?.data?.message ||
              error?.message ||
              self.$t("facilityPage.loadFailed"),
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    Preview_image() {
      this.releasePreviewObjectUrl();
      this.urlImg = URL.createObjectURL(this.FacilityImage);
      this.previewObjectUrl = this.urlImg;
    },

    releasePreviewObjectUrl() {
      if (!this.previewObjectUrl) return;
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    },

    async searchDataFacility() {
      this.page = 1;
      await this.getDataFacility();
    },

    getDataDropdownLocation() {
      let self = this;
      axios
        .get(`${self.url}Facility/getDataDropdownLocation`)
        .then(function (response) {
          self.throwIfSessionExpired(response);
          if (response.data.status == 0) {
            self.LocationList = response.data.data;
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: self.$t("facilityPage.error"),
            width: 900,
            text:
              error?.response?.data?.message ||
              error?.message ||
              self.$t("facilityPage.locationLoadFailed"),
          });
        });
    },

    getDataDropdownZone() {
      let self = this;
      return axios
        .get(`${self.url}Facility/getDataDropdownZone`)
        .then(function (response) {
          self.throwIfSessionExpired(response);
          if (response.data.status == 0) {
            self.ZoneList = response.data.data;
          } else {
            throw new Error(
              response.data.message || self.$t("facilityPage.floorLoadFailed"),
            );
          }
        })
        .catch(function (error) {
          self.showRequestError(
            error,
            self.$t("facilityPage.floorLoadFailed"),
          );
        });
    },

    async getDetailDataFacilityByID(id) {
      this.releasePreviewObjectUrl();
      this.FacilityDialog = false;
      this.UtilityDataReady = false;
      this.UtilityDataError = "";
      this.LoadingDialog = true;
      const temp = {
        facilityID: id,
      };
      try {
        const [facilityResponse, utilityResponse] = await Promise.all([
          axios.post(`${this.url}Facility/getDetailDataFacilityByID`, temp),
          axios.get(`${this.url}Facility/getDataUtilityList`),
        ]);
        this.throwIfSessionExpired(facilityResponse);
        this.throwIfSessionExpired(utilityResponse);

        if (utilityResponse.data.status !== 0 || !Array.isArray(utilityResponse.data.data)) {
          throw new Error(utilityResponse.data.message || this.$t("facilityPage.utilityLoadFailed"));
        }
        if (facilityResponse.data.status !== 0) {
          throw new Error(facilityResponse.data.message || this.$t("facilityPage.loadFailed"));
        }
        if (facilityResponse.data.status == 0) {
          const facility = facilityResponse.data.data;
          const facilityUtilities = Array.isArray(facility.utilityDetail)
            ? facility.utilityDetail
            : [];
          const allUtilities = utilityResponse.data.data;
          this.AllUtilityList = allUtilities;
          const facilityUtilityMap = new Map(
            facilityUtilities.map((utility) => [
              String(utility.utilityID),
              utility,
            ]),
          );

          this.FacilityID = id;
          this.FacilityName = facility.facilityName;
          this.RoomType =
            facility.roomType == 1
              ? "Room"
              : facility.roomType == 2
              ? "Space"
              : null;
          this.EventType = facility.eventType == 1 ? "Reserve" : null;
          this.Location = facility.location;
          this.ZoneID = facility.zoneID;
          this.OpenTime = this.formatDateTimeInput(facility.openTime);
          this.EndTime = this.formatDateTimeInput(facility.endTime);
          this.FacilityImage = null;
          this.UtilitysList = allUtilities.map((utility) => {
            const facilityUtility = facilityUtilityMap.get(
              String(utility.utilityID),
            );
            return {
              ...facilityUtility,
              ...utility,
              amount:
                facilityUtility && facilityUtility.amount != null
                  ? Number(facilityUtility.amount)
                  : 0,
            };
          });
          this.urlImg = facility.image || defaultFacilityPreview;
          this.base64 = facility.image || "";
          this.Active = facility.active == true ? "Active" : "InActive";
          this.UtilityDataReady = true;
          this.FacilityDialog = true;
        }
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          this.$t("facilityPage.loadFailed");
        Swal.fire({
          icon: "error",
          title: this.$t("facilityPage.error"),
          width: 900,
          text: message,
        });
      } finally {
        this.LoadingDialog = false;
      }
    },

    openEditDataFacility(id) {
      let self = this;
      self.isCreateMode = false;
      self.getDetailDataFacilityByID(id);
    },

    formatDateTimeInput(value) {
      if (!value) return null;
      const dateTime = String(value);
      if (dateTime.includes("T")) return dateTime.slice(0, 16);

      const time = dateTime.slice(0, 5);
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const day = String(currentDate.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}T${time}`;
    },

    createBase64Image(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () =>
          reject(new Error(this.$t("facilityPage.imageReadFailed")));
        reader.onload = (event) => {
          const image = new Image();
          image.onerror = () =>
            reject(new Error(this.$t("facilityPage.invalidImageFile")));
          image.onload = () => {
            const canvas = document.createElement("canvas");
            const targetWidth = Math.min(400, image.width);
            const scaleFactor = targetWidth / image.width;
            canvas.width = targetWidth;
            canvas.height = Math.round(image.height * scaleFactor);
            const context = canvas.getContext("2d");
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            this.base64 = canvas.toDataURL("image/jpeg", 0.85);
            resolve(this.base64);
          };
          image.src = event.target.result;
        };
        reader.readAsDataURL(file);
      });
    },

  },
};
</script>
<style scoped>
* >>> .v-data-table-header {
  background-color: black;
  color: #ffffff !important;
}
* >>> .v-data-table-header th {
  font-size: 14px !important;
  color: #ffffff !important;
}

@keyframes load {
  0% {
    transform: scaleY(0.4);
  }
  20% {
    transform: scaleY(1);
  }
  40% {
    transform: scaleY(0.4);
  }
  100% {
    transform: scaleY(0.4);
  }
}
@-moz-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-webkit-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@-o-keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}
.rect2 {
  animation-delay: -1.2s;
}
.rect3 {
  animation-delay: -1.1s;
}
.rect4 {
  animation-delay: -1s;
}
.rect5 {
  animation-delay: -0.9s;
}

.label-col {
  text-align: right;
}

@media (max-width: 960px) {
  .label-col {
    text-align: left;
  }
}

.label-text {
  font-size: 18px;
  font-weight: 500;
  color: #555;
  margin: 8px 0;
}

.value-text {
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin: 8px 0;
}

.section-title {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
}

.room-card {
  border-radius: 30px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

/* IMAGE */
.image-wrapper {
  position: relative;
}

.room-image {
  border-radius: 30px 30px 0 0;
}

.image-gradient {
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 80px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(0, 40, 120, 0.4)
  );
}

.room-title {
  font-size: 32px;
  font-weight: 800;
  color: #0d3b8f;
}

.room-location {
  margin-top: 4px;
  font-size: 16px;
  color: #444;
}

.room-utilities {
  margin-top: 8px;
}

.utility-item {
  font-size: 14px;
  color: #e47d83;
  margin-right: 10px;
}

.room-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 15px;
}

.btn-booking {
  background: #0d3b8f !important;
  color: white !important;
  border-radius: 30px;
  padding: 8px 28px;
  text-transform: none;
  width: 130px;
}

.btn-all-booking {
  background: #ebeae9 !important;
  color: #0d3b8f !important;
  border-radius: 30px;
  padding: 8px 28px;
  text-transform: none;
  width: 130px;
}

.facility-card {
  border-radius: 16px;
}

.facility-header {
  background: linear-gradient(135deg, #1a1a1a, #333);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-title {
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.image-box {
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  position: relative;
}

.upload-overlay {
  padding: 10px;
}

.status-card {
  padding: 20px;
  border-radius: 10px;
}

.utility-card {
  border-radius: 10px;
}

.utility-table {
  border-radius: 10px;
}

.utility-table .v-data-table-header {
  background: #f5f7fb;
  font-weight: 600;
}

.facility-page {
  min-height: calc(100vh - 125px);
  padding: var(--workspace-content-top) 0 54px;
  background: #f6f6f6;
}

.facility-heading h1 {
  margin: 0;
  color: #111111;
  font-size: 30px;
  font-weight: 600;
  line-height: 36px;
}

.facility-heading p {
  margin: 1px 0 0;
  color: #111111;
  font-size: 16px;
}

.facility-toolbar {
  display: grid;
  grid-template-columns: minmax(420px, 660px) 157px;
  align-items: center;
  justify-content: center;
  gap: 23px;
  margin-bottom: 37px;
}

.facility-search {
  display: flex;
  height: 60px;
  padding: 7px 9px 8px 37px;
  align-items: center;
  border-radius: 60px;
  background: #ffffff;
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
}

.facility-search input {
  min-width: 0;
  flex: 1;
  border: 0;
  outline: 0;
  color: #191c1e;
  font-size: 20px;
}

.facility-search input::placeholder {
  color: #595959;
  opacity: 1;
}
.facility-search button {
  display: grid;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #00378e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.create-facility {
  height: 60px;
  border: 0;
  border-radius: 60px;
  background: #00378e;
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.2);
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
}

.facility-panel {
  box-sizing: border-box;
  min-height: 817px;
  padding: 58px 104px 36px;
  border-radius: 30px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.facility-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(260px, 430px));
  justify-content: space-between;
  gap: 38px;
}
.room-card {
  display: flex;
  min-width: 0;
  height: 636px;
  flex-direction: column;
  overflow: hidden;
  border-radius: 30px;
  background: #ffffff;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}
.room-cover {
  position: relative;
  height: 313px;
  flex: 0 0 313px;
  margin: 15px 15px 0;
  overflow: hidden;
  border-radius: 22px;
}
.room-cover > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.room-cover::after {
  position: absolute;
  inset: 34% 0 0;
  content: "";
  background: linear-gradient(transparent, #000);
}
.room-overlay {
  position: absolute;
  z-index: 1;
  right: 30px;
  bottom: 14px;
  left: 30px;
  display: flex;
  flex-direction: column;
  color: #ffffff;
}
.room-overlay h2 {
  margin: 0;
  font-size: 25px;
  font-weight: 600;
  line-height: 30px;
}
.room-overlay span {
  color: #c6c6c6;
  font-size: 16px;
  line-height: 20px;
}
.room-detail {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  padding: 24px 43px 39px;
}
.room-detail h3 {
  margin: 0 0 28px;
  color: #00368e;
  font-size: 15px;
  font-weight: 700;
}
.utility-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 31px 18px;
}
.utility-grid > div {
  display: grid;
  grid-template-columns: 22px auto;
  align-items: center;
  color: #00368e;
}
.utility-grid strong {
  font-size: 20px;
  line-height: 20px;
}
.utility-grid span {
  grid-column: 1 / -1;
  margin-top: 2px;
  color: rgba(0, 54, 142, 0.9);
  font-size: 10px;
  line-height: 12px;
}
.no-utility {
  margin: 0;
  color: #595959;
  font-size: 13px;
}
.room-actions {
  display: grid;
  width: 387px;
  grid-template-columns: 180px 180px;
  align-self: center;
  gap: 27px;
  margin-top: auto;
}
.room-actions .v-btn {
  width: auto;
  height: 46px;
  margin: 0;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
}
.delete-button {
  background: #ebeae9 !important;
  color: #00368e !important;
}
.edit-button {
  background: #00368e !important;
  color: #ffffff !important;
}
.empty-facility {
  display: flex;
  min-height: 620px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #657083;
}
.facility-pagination {
  position: relative;
  display: flex;
  height: 59px;
  align-items: center;
  justify-content: center;
  margin-top: 28px;
}
.page-size {
  position: absolute;
  right: -11px;
  width: 133px;
  max-width: 133px;
  border-radius: 30px;
}
.page-size ::v-deep .v-input__slot {
  min-height: 50px;
  border-radius: 30px !important;
  background: #f6f6f6 !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25) !important;
}
.facility-pagination ::v-deep .v-pagination__item,
.facility-pagination ::v-deep .v-pagination__navigation {
  min-width: 49px;
  width: 49px;
  height: 49px;
  border-radius: 10px;
}

@media (max-width: 1400px) {
  .facility-panel {
    padding-right: 40px;
    padding-left: 40px;
  }
  .facility-grid {
    gap: 24px;
  }
}

@media (max-width: 1050px) {
  .facility-grid {
    grid-template-columns: repeat(2, minmax(260px, 430px));
  }
}

@media (max-width: 700px) {
  .facility-page {
    padding: 24px 16px 40px;
  }
  .facility-toolbar {
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 28px;
  }
  .create-facility {
    width: 157px;
    justify-self: end;
  }
  .facility-panel {
    padding: 24px 16px;
    border-radius: 16px;
  }
  .facility-grid {
    grid-template-columns: 1fr;
  }
  .room-card {
    height: 610px;
  }
  .room-detail {
    padding-right: 24px;
    padding-left: 24px;
  }
  .room-actions {
    width: 100%;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .facility-pagination {
    height: 129px;
    margin-top: 20px;
    padding-bottom: 70px;
  }
  .page-size {
    right: 50%;
    bottom: 0;
    transform: translateX(50%);
  }
}

.facility-card {
  width: 1254px;
  height: 841px;
  overflow: hidden;
  border-radius: 30px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
}
.facility-header {
  display: flex;
  height: 63px;
  justify-content: center;
  padding: 0 24px !important;
  background: #00368e;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
}
.facility-header > button {
  position: absolute;
  right: 18px;
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  border: 0;
  background: transparent;
}
.edit-facility-content {
  display: grid;
  grid-template-columns: 748px 400px;
  grid-template-rows: 258px 20px 293px;
  column-gap: 36px;
  height: 664px;
  padding: 20px 32px 59px 38px !important;
}
.edit-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-content: start;
  gap: 18px 24px;
}
.edit-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.edit-field label {
  color: rgba(25, 28, 30, 0.9);
  font-size: 13px;
  font-weight: 500;
  line-height: 19.5px;
}
.edit-field ::v-deep .v-input__slot {
  min-height: 48px !important;
  padding: 0 17px !important;
  border-radius: 30px !important;
  background: #fff !important;
}
.edit-field ::v-deep fieldset {
  border-color: rgba(194, 198, 214, 0.3) !important;
}
.edit-field ::v-deep input,
.edit-field ::v-deep .v-select__selection {
  color: #4c4c4c;
  font-size: 16px;
}
.edit-field ::v-deep .v-select__slot .v-input__append-inner {
  align-self: center;
  margin-top: 0 !important;
}
.edit-field ::v-deep .v-select__slot .v-input__append-inner .v-icon {
  width: 28px;
  height: 28px;
  color: #00378e !important;
  font-size: 28px;
}
.edit-preview-column {
  grid-column: 2;
  grid-row: 1 / 4;
  display: flex;
  width: 400px;
  flex-direction: column;
  gap: 24px;
  margin-top: 25px;
}
.edit-image {
  position: relative;
  width: 100%;
  height: 313px;
  flex: 0 0 313px;
  overflow: hidden;
  border-radius: 20px 20px 30px 30px;
}
.edit-image::after {
  position: absolute;
  inset: 55% 0 0;
  content: "";
  background: linear-gradient(transparent, #000);
  pointer-events: none;
}
.edit-image > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.image-upload {
  position: absolute;
  z-index: 1;
  right: 15px;
  bottom: 15px;
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  cursor: pointer;
}
.image-upload input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}
.edit-utilities {
  grid-column: 1;
  grid-row: 3;
  height: 293px;
  overflow: auto;
  border: 1px solid #111;
  border-radius: 20px 20px 10px 10px;
}
.edit-utilities .utility-table {
  width: 100%;
  color: #4c4c4c;
  font-size: 12px;
}
.edit-utilities .utility-table ::v-deep .v-data-table__wrapper {
  max-height: 291px;
  overflow: auto;
}
.edit-utilities .utility-table ::v-deep table {
  min-width: 600px;
  table-layout: fixed;
}
.edit-utilities .utility-table ::v-deep .v-data-table-header {
  position: sticky;
  z-index: 1;
  top: 0;
  background: #00368e;
}
.edit-utilities .utility-table ::v-deep .v-data-table-header th {
  height: 63px;
  padding: 8px 16px !important;
  border-bottom: 0 !important;
  color: #fff !important;
  font-size: 18px;
  font-weight: 400;
  white-space: nowrap;
}
.edit-utilities .utility-table ::v-deep tbody td {
  height: 44px;
  padding: 8px 16px !important;
  border-bottom: 0 !important;
  color: #4c4c4c;
  font-size: 12px;
}
.edit-utilities .utility-table ::v-deep th:first-child,
.edit-utilities .utility-table ::v-deep td:first-child {
  width: 15%;
}
.edit-utilities .utility-table ::v-deep th:nth-child(2),
.edit-utilities .utility-table ::v-deep td:nth-child(2) {
  width: 45%;
}
.edit-utilities .utility-table ::v-deep th:nth-child(3),
.edit-utilities .utility-table ::v-deep td:nth-child(3) {
  width: 25%;
}
.edit-utilities .utility-table ::v-deep th:last-child,
.edit-utilities .utility-table ::v-deep td:last-child {
  width: 15%;
  text-align: center;
}
.edit-utilities .utility-table ::v-deep tbody tr {
  background: #fff;
}
.edit-utilities .utility-table ::v-deep tbody tr:nth-child(even) {
  background: #f6f6f6;
}
.edit-utilities .utility-table ::v-deep .utility-delete-action {
  display: inline-grid;
  width: 26px;
  height: 26px;
  place-items: center;
  border: 0;
  border-radius: 50%;
  background: #d00000;
}
.edit-utilities .utility-table ::v-deep .utility-delete-action img {
  display: block;
  width: 18px;
  height: 18px;
}
.edit-utilities .utility-table ::v-deep .utility-delete-action:focus-visible {
  outline: 3px solid #ffb74d;
  outline-offset: 3px;
}
.table-empty {
  padding: 24px 16px;
  color: #595959;
  text-align: center;
}
.edit-utilities table {
  width: 100%;
  border-collapse: collapse;
  color: #4c4c4c;
  font-size: 12px;
}
.edit-utilities thead {
  position: sticky;
  z-index: 1;
  top: 0;
  height: 63px;
  background: #00368e;
  color: #fff;
  font-size: 15px;
  font-weight: 400;
}
.edit-utilities th,
.edit-utilities td {
  padding: 8px 16px;
  text-align: left;
}
.edit-utilities th:first-child,
.edit-utilities td:first-child {
  width: 110px;
}
.edit-utilities th:nth-child(3),
.edit-utilities td:nth-child(3) {
  width: 190px;
}
.edit-utilities th:last-child,
.edit-utilities td:last-child {
  width: 100px;
  text-align: center;
}
.edit-utilities tbody tr {
  height: 44px;
  background: #fff;
}
.edit-utilities tbody tr:nth-child(even) {
  background: #f6f6f6;
}
.edit-utilities td button {
  border: 0;
  background: transparent;
}
.edit-utilities ::v-deep .v-input__slot {
  min-height: 33px !important;
  background: #e3e3e3 !important;
  box-shadow: none !important;
}
.edit-utilities ::v-deep input {
  text-align: center;
  font-size: 12px;
}
.facility-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
}
.facility-status button {
  width: 124px;
  height: 40px;
  border: 0;
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.18);
  color: #4c4c4c;
  font-size: 15px;
}
.facility-status button.selected {
  outline: 3px solid #00368e;
  outline-offset: -3px;
}
.edit-actions {
  height: 67px;
  justify-content: center;
  gap: 60px;
  padding: 12px 24px 0 !important;
}
.edit-actions .v-btn {
  width: 160px;
  height: 55px;
  border-radius: 28px;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
}
.cancel-edit {
  background: #ebeae9 !important;
  color: #00368e !important;
}
.save-edit {
  background: #00368e !important;
  color: #fff !important;
}
@media (max-width: 1000px) {
  .facility-card {
    display: block;
    width: 100%;
    height: auto;
    zoom: 1;
  }

  .facility-header,
  .edit-facility-content,
  .edit-actions {
    flex: none;
  }
  .edit-facility-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    height: auto;
    padding: 24px 24px 20px !important;
  }
  .edit-preview-column,
  .edit-utilities,
  .facility-status {
    grid-column: 1;
    grid-row: auto;
  }
  .edit-preview-column {
    width: 100%;
    margin-top: 24px;
  }
  .edit-utilities {
    margin-top: 24px;
  }
  .facility-status {
    padding: 12px 0;
  }
  .edit-actions {
    height: auto;
    gap: 18px;
    padding: 12px 24px 23px !important;
  }
}
@media (max-width: 600px) {
  .edit-fields {
    grid-template-columns: 1fr;
  }
  .edit-facility-content {
    padding: 24px 16px !important;
  }
  .edit-actions .v-btn {
    width: 130px;
  }
}

/* Compact desktop density; dialog and mobile sizing remain unchanged. */
@media (min-width: 701px) {
  .facility-card {
    display: flex;
    width: 1254px;
    height: 753px;
    flex-direction: column;
    border-radius: 20px !important;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18) !important;
  }

  .facility-header {
    flex: 0 0 63px;
    height: 63px;
    font-size: 20px;
  }

  .facility-header > button {
    right: 14px;
    width: 40px;
    height: 40px;
  }

  .edit-facility-content {
    grid-template-columns: 748px 400px;
    grid-template-rows: 258px 14px 293px;
    column-gap: 36px;
    height: 603px;
    flex: 0 0 603px;
    padding: 20px 32px 18px 38px !important;
  }

  .edit-fields {
    gap: 18px 24px;
  }

  .edit-field {
    gap: 6px;
  }

  .edit-field ::v-deep .v-input__slot {
    min-height: 48px !important;
  }

  .edit-field ::v-deep input,
  .edit-field ::v-deep .v-select__selection {
    font-size: 16px;
  }

  .edit-preview-column {
    width: 400px;
    gap: 24px;
    margin-top: 25px;
  }

  .edit-image {
    height: 313px;
    flex-basis: 313px;
    border-radius: 20px 20px 30px 30px;
  }

  .edit-utilities {
    height: 293px;
    border-radius: 20px 20px 10px 10px;
  }

  .edit-utilities .utility-table ::v-deep .v-data-table__wrapper {
    max-height: 291px;
  }

  .edit-utilities .utility-table ::v-deep .v-data-table-header th {
    height: 63px;
    padding: 8px 16px !important;
    font-size: 18px;
  }

  .edit-utilities .utility-table ::v-deep tbody td {
    height: 44px;
    padding: 8px 16px !important;
  }

  .facility-status {
    gap: 30px;
  }

  .facility-status button {
    width: 124px;
    height: 40px;
    font-size: 15px;
  }

  .edit-actions {
    height: 87px;
    flex: 0 0 87px;
    gap: 60px;
    padding: 8px 24px 24px !important;
  }

  .edit-actions .v-btn {
    width: 160px;
    height: 55px;
    border-radius: 28px;
    font-size: 18px;
  }

  .facility-page {
    min-height: calc(100vh - 104px);
    padding: 36px 0;
  }

  .facility-toolbar {
    grid-template-columns: minmax(360px, 560px) 126px;
    gap: 16px;
    margin-bottom: 26px;
  }

  .facility-search,
  .create-facility {
    height: 46px;
  }

  .facility-search {
    padding: 5px 6px 5px 22px;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
  }

  .facility-search input,
  .create-facility {
    font-size: 16px;
  }

  .facility-search button {
    width: 36px;
    height: 36px;
    flex-basis: 36px;
    box-shadow: none;
  }

  .facility-panel {
    min-height: 650px;
    padding: 32px 48px 26px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .facility-grid {
    gap: 24px;
  }

  .room-card {
    height: 500px;
    border-radius: 16px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
  }

  .room-cover {
    height: 230px;
    flex-basis: 230px;
    margin: 10px 10px 0;
    border-radius: 12px;
  }

  .room-overlay {
    right: 20px;
    bottom: 11px;
    left: 20px;
  }

  .room-overlay h2 {
    font-size: 20px;
    line-height: 25px;
  }

  .room-overlay span {
    font-size: 13px;
    line-height: 17px;
  }

  .room-detail {
    padding: 18px 24px 24px;
  }

  .room-detail h3 {
    margin-bottom: 18px;
    font-size: 14px;
  }

  .utility-grid {
    gap: 20px 14px;
  }

  .utility-grid strong {
    font-size: 17px;
  }

  .room-actions {
    width: 100%;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .room-actions .v-btn {
    height: 40px;
    font-size: 14px;
  }

  .empty-facility {
    min-height: 490px;
  }

  .facility-pagination {
    height: 44px;
    margin-top: 20px;
  }

  .page-size {
    width: 112px;
    max-width: 112px;
  }

  .page-size ::v-deep .v-input__slot {
    min-height: 42px;
  }

  .facility-pagination ::v-deep .v-pagination__item,
  .facility-pagination ::v-deep .v-pagination__navigation {
    width: 40px;
    min-width: 40px;
    height: 40px;
    border-radius: 8px;
  }
}

@media (min-width: 1300px) {
  .facility-card {
    zoom: 0.88;
  }
}

@media (min-width: 701px) and (max-width: 1299px) {
  .facility-card {
    width: 100%;
    height: auto;
  }

  .edit-facility-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    height: auto;
    padding: 24px 24px 20px !important;
  }

  .edit-preview-column,
  .edit-utilities,
  .facility-status {
    grid-column: 1;
    grid-row: auto;
  }

  .edit-preview-column {
    width: 100%;
    margin-top: 24px;
  }

  .edit-utilities {
    margin-top: 24px;
  }

  .facility-status {
    padding: 12px 0;
  }

  .edit-actions {
    height: auto;
    gap: 18px;
    padding: 12px 24px 23px !important;
  }
}
</style>
